import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { useGetCompanyInfoMutation } from "services/company";
import { selectUserData } from "@redux/reducers/user.slice";
import { isEmpty, isEqual } from "helpers";
import { userRoles } from "invariants";
import {
  useGetDashboardsMutation,
  useChangeDashboardOrderMutation,
} from "services/dashboard";
import { useGetParentMenuListMutation } from "services/parentMenu";
import { Dashboard, User, Company, MenuItem } from "types";

import {
  foundationNames,
  priceOptimizationNames,
  inventoryOptimizationNames,
} from "../../../../utils/utilsDashboard";

type ClientProviderProps = {
  children: React.ReactNode;
};

type clientContextType = {
  isEditMode: boolean;
  step: number;
  isSuperAdmin: boolean;
  userData: User;
  companyData: Company;
  onOrderChange: (v: string, i: number) => void;
  drawerTabs: Dashboard[];
  parentMenu: MenuItem[];
  newUrl: string;
  newParentName: string;
  newSubItemMenuName: string;
  newIconArry: string;
  hiddenTabs: Dashboard[];
  setDrawerTabs: (v: Dashboard[]) => void;
  setParentMenu: (v: MenuItem[]) => void;
  setNewUrl: (v: string) => void;
  setNewParentName: (v: string) => void;
  setNewSubItemMenuName: (v: string) => void;
  setNewIconArry: (v: string) => void;
  setHiddenTabs: (v: Dashboard[]) => void;
  setEditMode: (v: boolean) => void;
  setStep: (v: number) => void;
  companyId: string;
  isUserDataLoaded: boolean;
  isCompanyDataLoaded: boolean;
  dashboardId: string;
  getDashboards: (v?: boolean) => void;
  addItemToParentMenu: (v: MenuItem) => void;
  //testData below
  widgetsList: any;
  onRemoveWidget: any;
  onResetToDefault: any;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (v: boolean) => void;
};

const ClientContext = React.createContext<clientContextType>(
  {} as clientContextType
);

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const { data = {}, isSuccess: isUserDataLoaded } =
    useSelector(selectUserData);
  const { dashboardId = "", id: companyId = "" } = useParams();
  const navigate = useNavigate();

  const [fetchDashboards]: any = useGetDashboardsMutation();
  const [fetchParentMenuList]: any = useGetParentMenuListMutation();
  const [changeOrderForDashboard]: any = useChangeDashboardOrderMutation();

  const [
    fetchData,
    { isSuccess: isCompanyDataLoaded, data: companyData = {} },
  ] = useGetCompanyInfoMutation();

  const [isEditMode, setEditMode] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);
  const [drawerTabs, setDrawerTabs] = useState<Dashboard[] | []>([]);
  const [parentMenu, setParentMenu] = useState<MenuItem[] | []>([]);
  const [newParentName, setNewParentName] = useState<string>("");
  const [newSubItemMenuName, setNewSubItemMenuName] = useState<string>("");
  const [newIconArry, setNewIconArry] = useState<string>("");
  const [newUrl, setNewUrl] = useState<string>("");
  const [hiddenTabs, setHiddenTabs] = useState<Dashboard[] | []>([]);
  const [widgetsList, setWidgetsList] = useState<any>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  const transformMenuData = (data: any): any => {
    return data.map((menu: any) => {
      const initialArray = menu.icons
        .replace(/\[|\]/g, "")
        .split(",")
        .map((icon: any) => icon.trim());
      const iconArray = initialArray.map((item: any) => item.replace(/'/g, ""));

      const submenus = menu.submenus.map((submenu: any, index: any) => {
        let link = "";
        if (submenu.router && submenu.router.length > 4) {
          link = submenu.router;
        } else if (submenu.link && submenu.link.length > 4) {
          link = submenu.link;
        }

        return {
          order_index: index,
          name: submenu.name,
          is_hidden: false,
          link,
          id: submenu.id.toString(),
          dashboard_id: submenu.dashboard_id,
          is_external_dashboard: true,
          updated: false,
        };
      });

      return {
        id: menu.id,
        name: menu.name,
        icon: iconArray,
        updated: false,
        submenus,
      };
    });
  };

  const onOrderChange = useCallback(
    (tabId: string, newPositionIndex: number) => {
      changeOrderForDashboard({
        companyId,
        dashboardId: tabId,
        newPositionIndex,
      });
    },
    [companyId, changeOrderForDashboard]
  );

  const getDashboards = useCallback(
    (redirectAnyway: boolean = false) => {
      fetchDashboards(companyId).then(
        ({ data = [] }: { data: Dashboard[] }) => {
          const transformedData = data.map((item: Dashboard) => ({
            ...item,
            id: item.id.toString(),
          }));

          setDrawerTabs(
            transformedData.filter(({ is_hidden }: Dashboard) => !is_hidden)
          );
          setHiddenTabs(
            transformedData.filter(({ is_hidden }: Dashboard) => is_hidden)
          );

          if ((!dashboardId || redirectAnyway) && !isEmpty(data)) {
            navigate(`/user/company/${companyId}/${data[0]?.id}`);
          }
        }
      );
    },
    // eslint-disable-next-line
    [fetchDashboards, dashboardId, companyId]
  );

  const getFilteredDashboards = async (
    companyId: string
  ): Promise<Dashboard[]> => {
    try {
      const { data = [] }: { data: Dashboard[] } = await fetchDashboards(
        companyId
      );

      const transformedData = data.map((item: Dashboard) => ({
        ...item,
        id: item.id.toString(),
      }));

      return transformedData.filter(({ is_hidden }: Dashboard) => !is_hidden);
    } catch (error) {
      console.error("Error fetching dashboards:", error);
      return [];
    }
  };

  const getDashboardsMenu = async (companyId: string): Promise<any[]> => {
    try {
      const { data = [] }: { data: Dashboard[] } = await fetchDashboards(
        companyId
      );

      const transformedData = data.map((item: Dashboard) => ({
        ...item,
        id: item.id.toString(),
      }));

      const filterData = transformedData.filter(
        ({ is_hidden }: Dashboard) => !is_hidden
      );

      let foundations: any = [];
      let priceOpts: any = [];
      let inventoryOpts: any = [];

      filterData.forEach((item) => {
        if (item.name.includes("§")) {
          const parts = item.name.split("§");
          const order = parts.length >= 3 ? parts[0] : null;
          const prefix = parts.length >= 3 ? parts[1] : parts[0];
          const actualName = parts.length >= 3 ? parts[2] : parts[1];

          const cleanedOrder = order ? Number(order) : Infinity;

          switch (prefix.trim()) {
            case "1":
              foundations.push({
                ...item,
                name: actualName.trim(),
                order_index: cleanedOrder,
                is_hidden: parts.length === 4 && parts[3] === "true",
              });
              break;
            case "2":
              priceOpts.push({
                ...item,
                name: actualName.trim(),
                order_index: cleanedOrder,
                is_hidden: parts.length === 4 && parts[3] === "true",
              });
              break;
            case "3":
              inventoryOpts.push({
                ...item,
                name: actualName.trim(),
                order_index: cleanedOrder,
                is_hidden: parts.length === 4 && parts[3] === "true",
              });
              break;
            default:
              break;
          }
        } else {
          if (foundationNames.includes(item.name.trim())) {
            foundations.push(item);
          }
          if (priceOptimizationNames.includes(item.name.trim())) {
            priceOpts.push(item);
          }
          if (inventoryOptimizationNames.includes(item.name.trim())) {
            inventoryOpts.push(item);
          }
        }
      });

      const sortByOrder = (a: any, b: any) => {
        const aOrder =
          typeof a.order_index === "number" ? a.order_index : Infinity;
        const bOrder =
          typeof b.order_index === "number" ? b.order_index : Infinity;
        return aOrder - bOrder;
      };

      return [
        foundations.sort(sortByOrder),
        priceOpts.sort(sortByOrder),
        inventoryOpts.sort(sortByOrder),
      ];
    } catch (error) {
      console.error("Error fetching dashboards:", error);
      return [];
    }
  };

  const transformMenuDataWithDefaultMenu = async (
    dashboards: any[],
    transformedData: any[]
  ): Promise<any[]> => {
    const missingDashboards = dashboards.filter(
      (dashboard) =>
        !transformedData.some((data: any) =>
          data.submenus.some(
            (submenu: any) => submenu.dashboard_id === dashboard.id
          )
        )
    );

    return missingDashboards;
  };

  const getParentMenuList = useCallback(
    async (redirectAnyway: boolean = false) => {
      try {
        const dashboardsMenu = await getDashboardsMenu(companyId);

        const foundation = {
          id: 100000001,
          name: "Foundation",
          icon: ["barChartBlack", "barChart"],
          updated: false,
          submenus: dashboardsMenu[0].map((dashboard: any) => ({
            order_index: dashboard.order_index,
            name: dashboard.name,
            is_hidden: dashboard.is_hidden,
            link: dashboard.link,
            id: dashboard.id,
            dashboard_id: dashboard.id,
            is_external_dashboard: dashboard.is_external_dashboard,
            updated: false,
          })),
        };

        const priceOptimization = {
          id: 100000002,
          name: "Price Optimization",
          icon: ["trendingUpBlack", "trendingUp"],
          updated: false,
          submenus: dashboardsMenu[1].map((dashboard: any) => ({
            order_index: dashboard.order_index,
            name: dashboard.name,
            is_hidden: dashboard.is_hidden,
            link: dashboard.link,
            id: dashboard.id,
            dashboard_id: dashboard.id,
            is_external_dashboard: dashboard.is_external_dashboard,
            updated: false,
          })),
        };

        const inventoryOptimization = {
          id: 100000003,
          name: "Inventory Optimization",
          icon: ["boxBlack", "box"],
          updated: false,
          submenus: dashboardsMenu[2].map((dashboard: any) => ({
            order_index: dashboard.order_index,
            name: dashboard.name,
            is_hidden: dashboard.is_hidden,
            link: dashboard.link,
            id: dashboard.id,
            dashboard_id: dashboard.id,
            is_external_dashboard: dashboard.is_external_dashboard,
            updated: false,
          })),
        };

        const mergedArray = [
          foundation,
          priceOptimization,
          inventoryOptimization,
        ];

        setParentMenu(mergedArray);
      } catch (error) {
        console.error("Error fetching parent menu list:", error);
      }
    },
    [fetchParentMenuList, dashboardId, companyId]
  ); // eslint-disable-line

  const addItemToParentMenu = (menuItems: MenuItem) => {
    updateParentMenu(menuItems);
  };

  const updateParentMenu = (menuItem: MenuItem) => {
    setParentMenu((prevParentMenu) => {
      const existingItemIndex = prevParentMenu.findIndex(
        (item) => item.id === menuItem.id
      );
      if (existingItemIndex !== -1) {
        const updatedParentMenu = [...prevParentMenu];
        updatedParentMenu[existingItemIndex] = {
          ...updatedParentMenu[existingItemIndex],
          submenus: [
            ...updatedParentMenu[existingItemIndex].submenus,
            ...menuItem.submenus,
          ],
        };
        return updatedParentMenu;
      } else {
        return [...prevParentMenu, menuItem];
      }
    });
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      if (isUserDataLoaded) {
        if (data?.role === "super_admin") {
          fetchData({ companyId });
        }
        getParentMenuList();
      }
    };

    fetchDataAsync(); // Call the async function

    // eslint-disable-next-line
  }, [companyId, isUserDataLoaded, isEditMode]);

  // useEffect(() => {
  // if (drawerTabs[0]?.id === dashboardId) {
  //   setWidgetsList([])
  // } else {
  //   setWidgetsList([])
  // }
  // }, [dashboardId, drawerTabs])

  const onRemoveWidget = useCallback(
    (index: number) => {
      const newData = widgetsList.filter((item: any, i: number) => i !== index);

      setWidgetsList(newData);
    },
    [widgetsList]
  );

  const onResetToDefault = useCallback((index: number) => {
    setWidgetsList([]);
  }, []);

  const contextValue = useMemo<clientContextType>(
    () => ({
      companyId,
      dashboardId,
      isEditMode,
      step,
      drawerTabs,
      parentMenu,
      newUrl,
      newParentName,
      newSubItemMenuName,
      newIconArry,
      hiddenTabs,
      companyData,
      isUserDataLoaded,
      onOrderChange,
      onResetToDefault,
      setDrawerTabs,
      setParentMenu,
      setNewUrl,
      setNewParentName,
      setNewSubItemMenuName,
      setNewIconArry,
      setHiddenTabs,
      getDashboards,
      addItemToParentMenu,
      isSuperAdmin: isEqual(data?.role, userRoles.super_admin),
      userData: data,
      setEditMode,
      setStep,
      isCompanyDataLoaded,
      widgetsList,
      onRemoveWidget,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
    }),
    [
      companyId,
      dashboardId,
      isEditMode,
      step,
      setEditMode,
      setStep,
      onResetToDefault,
      data,
      companyData,
      drawerTabs,
      parentMenu,
      newUrl,
      newParentName,
      newSubItemMenuName,
      newIconArry,
      isUserDataLoaded,
      hiddenTabs,
      onOrderChange,
      widgetsList,
      onRemoveWidget,
      setDrawerTabs,
      setParentMenu,
      setNewUrl,
      setNewParentName,
      setNewSubItemMenuName,
      setNewIconArry,
      setHiddenTabs,
      getDashboards,
      addItemToParentMenu,
      isCompanyDataLoaded,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
    ]
  );

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export default ClientContext;
